import { Alert } from "@mui/material";
import dayjs from "dayjs";
import { useIntl } from "react-intl";

import {
  DetailsList,
  DetailsListItem,
} from "@/features/trips/components/DetailsList";
import { ImageCarousel } from "@/features/trips/components/ImageCarousel";
import { RoomList } from "@/features/trips/components/RoomList";
import type { TripBooking } from "@/features/trips/types/types";

type Props = {
  trip: TripBooking;
};

export function OverviewCard({ trip }: Props) {
  const intl = useIntl();

  return (
    <div className="rounded-3xl bg-white p-1">
      <ImageCarousel
        imagesUrls={trip.package.imageUrls || []}
        imageRatio="3/2"
      />

      <div className="p-3">
        <div className="mb-1 text-xl font-medium text-text-title">
          {trip.package.name}
        </div>
        <div className="text-sm text-text-tertiary">
          {trip.package.subtitle}
        </div>

        <Alert
          severity="info"
          sx={{
            my: "calc(var(--spacing)*3)",
          }}
        >
          <div className="mb-1 text-sm font-medium text-black">
            {intl.formatMessage({
              defaultMessage:
                "Full trip details will be shared after confirmation",
              description: "Heading of the booking information notice card",
            })}
          </div>
          <div className="text-sm text-text-placeholder">
            {intl.formatMessage({
              defaultMessage:
                "Our team will contact you with the flight & hotel details, and other trip information or requirements after confirmation",
              description:
                "Body text in the booking information notice card on the trip booking page",
            })}
          </div>
        </Alert>

        <DetailsList>
          <DetailsListItem
            title={intl.formatMessage({
              defaultMessage: "Booked on",
              description: "Label for the date the trip booking was confirmed",
            })}
            value={dayjs(trip.bookedAt).format("D MMMM, YYYY")}
          />

          {/* to add spacing */}
          <DetailsListItem title="" value="" />

          <DetailsListItem
            title={intl.formatMessage({
              defaultMessage: "Travel start date",
              description: "Label for the trip start / departure date",
            })}
            value={dayjs(trip.startDate).format("D MMMM, YYYY")}
          />

          <DetailsListItem
            title={intl.formatMessage({
              defaultMessage: "Travel end date",
              description: "Label for the trip end / return date",
            })}
            value={dayjs(trip.startDate)
              .add(trip.package.durationInDays, "day")
              .format("D MMMM, YYYY")}
          />

          <DetailsListItem
            title={intl.formatMessage({
              defaultMessage: "Travel length",
              description: "Label for the trip duration / length",
            })}
            value={intl.formatMessage(
              {
                defaultMessage:
                  "{nights} {nights, plural, one {night} other {nights}} / {days} {days, plural, one {day} other {days}}",
                description: "Trip duration label (e.g. '7 nights')",
              },
              {
                nights: trip.package.durationInDays - 1,
                days: trip.package.durationInDays,
              },
            )}
          />

          <DetailsListItem
            title={intl.formatMessage({
              defaultMessage: "Flight operator",
              description:
                "Label for the airline / flight operator shown on the trip details page",
            })}
            value={trip.package.airline}
          />

          <DetailsListItem
            title={intl.formatMessage({
              defaultMessage: "Cities",
              description: "Label for the list of cities visited during a trip",
            })}
            value={trip.package.cities}
          />

          <DetailsListItem
            title={intl.formatMessage({
              defaultMessage: "Countries",
              description: "Label for the countries visited during a trip",
            })}
            value={trip.package.countries.map((c) => c.name).join(", ")}
          />
        </DetailsList>

        <div className="mt-3 mb-1 text-sm text-text-disabled-primary">
          {intl.formatMessage({
            defaultMessage: "Booked rooms",
            description:
              "Section heading for room configuration in the trip details summary",
          })}
        </div>
        <RoomList
          rooms={{
            adultInDoubleRoom: trip.numberOfAdultsDoubleRoom,
            adultInSingleRoom: trip.numberOfAdultsSingleRoom,
            childWithBed: trip.numberOfChildrenWithBed,
            childWithoutBed: trip.numberOfChildrenWithoutBed,
            infant: trip.numberOfInfants,
          }}
        />

        <div className="mt-5 text-sm">{trip.package.description}</div>
      </div>
    </div>
  );
}
